import React, { useMemo } from "react";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { CRMChannel } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { InstagramAccountCard } from "../../components/InstagramAccountCard";
import { WhatsappAccountCard } from "../../components/WhatsappAccountCard";
import { WebChatBotAccountCard } from "../../components/WebChatBotAccountCard/WebChatBotAccountCard";
import { TwilioAccountCard } from "../../components/TwilioAccountCard/TwilioAccountCard";
import { CRMDrawerTabsLayout } from "../../components/CRMDrawerTabsLayout";
import { LiveChatAppAccountCard } from "../../components/LiveChatAppAccountCard";
import { GmailAccountCard } from "../../components/GmailAccountCard";
import { useEmailAccount } from "../../../../providers/EmailAccountProvider/useEmailAccount";

const channelComponents = {
  [CRMChannel.instagram]: InstagramAccountCard,
  [CRMChannel.whatsapp]: WhatsappAccountCard,
  [CRMChannel.chatbot]: WebChatBotAccountCard,
  [CRMChannel.twilio]: TwilioAccountCard,
  [CRMChannel.email]: GmailAccountCard,
};

export const ConnectedAccountListScreen: React.FC = () => {
  const { t } = useTranslations();
  const allChannels = useMemo(() => Object.values(CRMChannel), []);

  const { isCx } = useAppVariant();
  const { entity } = useEntitySelectedProvider();
  const { isConnected: isEmailConnected } = useEmailAccount();

  // Gmail is hidden on org/business workspaces unless it's already connected.
  const shouldHideGmailCard = entity.type === "org" && !isEmailConnected;

  const content = (
    <div className="jugl__custom-scrollbar flex h-full min-h-0 w-full flex-col items-center overflow-y-auto p-10 pb-20">
      <div className="flex w-full flex-col items-center gap-8">
        <div className="flex flex-col gap-2 text-center">
          <span className="font-secondary text-2xl font-medium text-black">
            {t({
              id: "client-support-page.connected-channels",
              defaultMessage: "Connected Channels",
            })}
          </span>
          <span className="font-secondary text-[#828282]">
            {t({
              id: "client-support-page.make-communication-with-customers-easy",
              defaultMessage:
                "Make the communication with your Customers easy ✨",
            })}
          </span>
        </div>
        <div className="grid w-max grid-cols-1 justify-items-center gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {allChannels.map((channel) => {
            const Component = channelComponents[channel];
            if (!Component) return null;
            if (channel === CRMChannel.email && shouldHideGmailCard) {
              return null;
            }

            return (
              <Component
                key={channel}
                hideReconnectOption
                isClickable
                variant="tile"
              />
            );
          })}
          <LiveChatAppAccountCard isClickable variant="tile" />
        </div>
      </div>
    </div>
  );

  if (isCx) {
    return content;
  }

  return <CRMDrawerTabsLayout>{content}</CRMDrawerTabsLayout>;
};
